// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

/**
 * CSS class applied to non-splittable report blocks. Queried by the PDF
 * generator to snap page cuts to block boundaries instead of splitting mid-card.
 */
export const NO_SPLIT_CLASS = "sar-no-split";

/**
 * CSS class that forces a page break immediately before the element. Queried
 * by the PDF generator to ensure the element always begins at the top of a page.
 * Unlike NO_SPLIT_CLASS, the element's own content may still be split across
 * subsequent pages.
 */
export const PAGE_START_CLASS = "sar-page-start";

/**
 * CSS class applied to a section's title container when it is a continuation
 * of the preceding section. The PDF generator hides this heading when the
 * continuation section lands on the same page as its primary — it only appears
 * when an actual page break separates the two.
 */
export const CONTINUATION_HEADER_CLASS = "sar-continuation-header";

/** Vertical gap between top-level report blocks (sections, charge cards). */
export const BLOCK_GAP = 30;

/** Vertical gap between label-value field rows inside a charge card. */
export const FIELD_ROW_GAP = 4;

/** Horizontal gap between officer info columns in the Requested Of section. */
export const SECTION_COLUMN_GAP = 16;

/** Horizontal gap between client identifier chips. */
export const CHIP_GAP = 10;

/** Left padding applied to each column in the charge card body. */
export const CHARGE_COLUMN_PADDING = 10;

/** Gap between an icon and its adjacent label/content column. */
export const ICON_LABEL_GAP = 5;

/**
 * Data attribute on the learn-more banner inside <tfoot>. The PDF exporter
 * queries this selector to show the banner only on the last-page footer capture.
 */
export const LEARN_MORE_BANNER_ATTR = "data-sar-learn-more-banner";

/**
 * Fallback text shown in the Recommendation section when the defendant declined
 * to participate. Shared by the DOM report (ReportRecommendation) and the
 * react-pdf template (RecommendationAndPlan) so the wording stays in one place.
 */
export const DECLINED_TEXT =
  "This officer is unable to complete this section due to the fact that the defendant declined to participate in the assessment process.";
